import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'
import AdminUsers from './AdminUsers'
import AdminSettings from './AdminSettings'
import AdminSponsors from './AdminSponsors'
import AdminMessages from './AdminMessages'
import AdminReports from './AdminReports'

export default function AdminPanel({ session, onExit, appSettings, onSettingsSaved }) {
  const [activeTab, setActiveTab] = useState('reports')
  const [profile, setProfile] = useState(null)
  const [checking, setChecking] = useState(true)
  const [authorized, setAuthorized] = useState(false)

  const checkAdminAccess = useCallback(async () => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .single()

    if (error || !data) {
      setAuthorized(false)
      setChecking(false)
      return
    }

    setProfile(data)
    if (data.role === 'admin' || data.role === 'superadmin') {
      setAuthorized(true)
    } else {
      setAuthorized(false)
    }
    setChecking(false)
  }, [session.user.id])

  useEffect(() => {
    checkAdminAccess()
  }, [checkAdminAccess])

  const tabs = [
    { id: 'reports', label: '📊 Reports', superonly: false },
    { id: 'users', label: '👥 Users', superonly: false },
    { id: 'settings', label: '⚙️ App Settings', superonly: true },
    { id: 'sponsors', label: '🤝 Sponsors', superonly: false },
    { id: 'messages', label: '📢 Messages', superonly: false },
  ]

  const visibleTabs = tabs.filter(t => !t.superonly || profile?.role === 'superadmin')

  // Loading
  if (checking) {
    return (
      <div style={{
        minHeight: '100vh', background: 'var(--bg)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '1rem'
      }}>
        <div style={{ fontSize: '2rem' }}>⏳</div>
        <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontFamily: 'monospace' }}>Checking admin access...</div>
      </div>
    )
  }

  // Not authorized
  if (!authorized) {
    return (
      <div style={{
        minHeight: '100vh', background: 'var(--bg)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '1rem', padding: '2rem'
      }}>
        <div style={{ fontSize: '3rem' }}>🚫</div>
        <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '1.5rem', fontWeight: '700', margin: 0, color: 'var(--text)' }}>Access Denied</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>
          You don't have admin access to DebtCrush.<br />
          Contact the super admin to get access.
        </p>
        <button
          onClick={onExit}
          style={{
            background: '#0d0d0d', color: '#f5f0e8',
            border: 'none', padding: '0.6rem 1.5rem',
            borderRadius: '6px', cursor: 'pointer',
            fontFamily: 'monospace', fontSize: '0.8rem',
            textTransform: 'uppercase', letterSpacing: '0.05em'
          }}
        >
          ← Back to App
        </button>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>

      {/* ADMIN HEADER */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '1rem 2rem', borderBottom: '1px solid #d4cfc5',
        background: '#0d0d0d', position: 'sticky', top: 0, zIndex: 50
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.3rem', fontWeight: '900', color: '#f5f0e8' }}>
            Debt <span style={{ color: '#c9a84c', fontStyle: 'italic' }}>Crush</span>
          </div>
          <span style={{
            fontFamily: 'monospace', fontSize: '0.65rem',
            background: '#c9a84c', color: '#0d0d0d',
            padding: '0.2rem 0.6rem', borderRadius: '3px',
            fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em'
          }}>
            {profile?.role === 'superadmin' ? '⭐ Super Admin' : '🔧 Admin'}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '0.82rem', color: '#8a8278', fontFamily: 'monospace' }}>
            {profile?.full_name || session.user.email}
          </span>
          <button
            onClick={onExit}
            style={{
              background: 'transparent', color: '#8a8278',
              border: '1px solid #3d3d3d', padding: '0.4rem 0.8rem',
              borderRadius: '4px', fontFamily: 'monospace',
              fontSize: '0.7rem', cursor: 'pointer'
            }}
          >
            ← Back to App
          </button>
        </div>
      </header>

      {/* ADMIN TABS */}
      <div style={{
        display: 'flex', borderBottom: '2px solid #d4cfc5',
        background: '#1a1a1a', overflowX: 'auto'
      }}>
        {visibleTabs.map(tab => (
          <div
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.85rem 1.4rem',
              fontFamily: 'monospace', fontSize: '0.72rem',
              fontWeight: '500', textTransform: 'uppercase',
              letterSpacing: '0.08em', cursor: 'pointer',
              color: activeTab === tab.id ? '#c9a84c' : '#8a8278',
              borderBottom: activeTab === tab.id ? '2px solid #c9a84c' : '2px solid transparent',
              marginBottom: '-2px', whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* ADMIN CONTENT */}
      <main style={{ maxWidth: '1300px', margin: '0 auto', padding: '2rem 1.5rem 4rem' }}>
        {activeTab === 'reports' && <AdminReports session={session} />}
        {activeTab === 'users' && <AdminUsers session={session} profile={profile} />}
        {activeTab === 'settings' && profile?.role === 'superadmin' && <AdminSettings session={session} />}
        {activeTab === 'sponsors' && <AdminSponsors session={session} />}
        {activeTab === 'messages' && <AdminMessages session={session} />}
      </main>

    </div>
  )
}